/**
 * EditorLayoutMenu — picks how the editor and its companion view ("preview":
 * the live board, a process diagram, …) share a tab. Lives in the tab strip's
 * trailing slot, shown only on tabs that have an editor. The choice is
 * remembered per tab; which options appear is per tab kind (see
 * `VIEW_MODE_BY_KIND`) and passed in as `options`.
 *
 *   editor-only   editor fills; view hidden (kept mounted)
 *   view-only     view fills;   editor hidden
 *   split-right   editor left  | view right
 *   split-bottom  editor top   / view bottom
 *   split-top     view top     / editor bottom
 *
 * Built on Radix `DropdownMenu` (the house overlay standard) — open/close,
 * dismiss, focus, keyboard nav, positioning and portalling are the library's.
 */
import { type ReactElement } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { PanelRight, PanelBottom, PanelTop, Square, Eye, Check, type LucideIcon } from "lucide-react";
import { useTranslation } from "../../i18n";

export type EditorLayout =
  | "editor-only"
  | "view-only"
  | "split-right"
  | "split-bottom"
  | "split-top";

export const EDITOR_LAYOUT_DEFAULT: EditorLayout = "split-right";

const OPTIONS: readonly { value: EditorLayout; labelKey: string; Icon: LucideIcon }[] = [
  { value: "editor-only", labelKey: "editorOnly", Icon: Square },
  { value: "view-only", labelKey: "viewOnly", Icon: Eye },
  { value: "split-right", labelKey: "splitRight", Icon: PanelRight },
  { value: "split-bottom", labelKey: "splitBottom", Icon: PanelBottom },
  { value: "split-top", labelKey: "splitTop", Icon: PanelTop },
];

function iconFor(layout: EditorLayout): LucideIcon {
  return OPTIONS.find((o) => o.value === layout)?.Icon ?? PanelRight;
}

export function EditorLayoutMenu({
  value,
  onChange,
  options,
}: {
  readonly value: EditorLayout;
  readonly onChange: (next: EditorLayout) => void;
  /** Which layouts to offer, in order. Defaults to all. */
  readonly options?: readonly EditorLayout[];
}): ReactElement {
  const { t } = useTranslation("editorLayout");
  const shown = options
    ? OPTIONS.filter((o) => options.includes(o.value))
    : OPTIONS;
  const Trigger = iconFor(value);
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label={t("triggerAriaLabel")}
        title={t("triggerTitle")}
        className="flex h-6 w-6 items-center justify-center rounded text-slate-400 outline-none transition-colors hover:bg-slate-800 hover:text-slate-200 data-[state=open]:bg-slate-800 data-[state=open]:text-slate-200"
      >
        <Trigger className="h-4 w-4" aria-hidden="true" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="z-overlay min-w-44 overflow-hidden rounded-md border border-slate-700 bg-slate-900 py-1 text-xs shadow-xl shadow-black/50"
        >
          <DropdownMenu.RadioGroup
            value={value}
            onValueChange={(v) => onChange(v as EditorLayout)}
          >
            {shown.map((o) => (
              <DropdownMenu.RadioItem
                key={o.value}
                value={o.value}
                className="flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left text-slate-300 outline-none data-[highlighted]:bg-slate-800 data-[highlighted]:text-slate-100"
              >
                <o.Icon className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden="true" />
                <span className="flex-1">{t(o.labelKey)}</span>
                <DropdownMenu.ItemIndicator>
                  <Check className="h-3.5 w-3.5 shrink-0 text-arch-400" aria-hidden="true" />
                </DropdownMenu.ItemIndicator>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
